// ── Logs / History ("/history") — Wave 10 (audit + distinct log views) ───────
// ONE honest read (GET /event-history) routed through useHonestQuery →
// <HonestState>. The page NEVER hand-rolls state branching: a permission-denied /
// unavailable / empty / error response renders via <HonestState>, never a plain
// empty table.
//
// CONTRACT TRUTH (apps/vjal-coaching-api/src): /event-history is org-wide for
// staff and scope-filtered for coach/company_admin/cohort_lead. Participant is
// denied before the read. There are NO dedicated invite/report-gen/participant/
// session log routes — the "distinct logs" are FILTERED VIEWS over this one
// read, keyed on the taxonomy `category` the backend stamps on each event.

// ── PARTICIPANT EXCLUSION (Wave 10 release gate) ─────────────────────────────
// A `participant` is DENIED the module entirely: route-denied (generic-not-found
// + no nav) AND this page fail-closes (canAccessLogsModule) so the read NEVER
// fires for a participant. Coach/company_admin/cohort_lead reach the module and
// receive only their resolved scope from the backend.

import { Alert, Badge, CollectionToolbar, ProTable } from "@vjal/ui";
import { useMemo } from "react";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { focusedListOrgQuery, focusedListQueryKey } from "../../hooks/focused-route-prefetch";
import type { CoachingHistoryEventDto, ListResponse } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { canAccessLogsModule, canReadAuditLog } from "../../domain/logs-and-exports";
import { getLogView } from "./logs-helpers";
import { useLogsCollection } from "./logs-collection";

const services = getCoachingServices();

export function LogsPage({ role, routePath }: ProductScreenContext) {
  const moduleDenied = !canAccessLogsModule(role);
  const auditReadable = canReadAuditLog(role);
  const listQuery = focusedListOrgQuery();

  const query = useHonestQuery<ListResponse<CoachingHistoryEventDto>>(
    focusedListQueryKey("history.list", listQuery?.orgId ?? null),
    () => services.history.list(listQuery),
    { enabled: !moduleDenied }
  );

  const events = query.data?.items;
  const allRows = useMemo(() => events ?? [], [events]);
  const collection = useLogsCollection(allRows, routePath);
  const activeView = getLogView(collection.state.filters.view as string | undefined);

  if (moduleDenied) {
    return (
      <section aria-label="History" className="col-span-12" data-testid="logs-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You don't have access to the event history"
            message="The operational event history and audit log are for coaching staff. There's no operational history on your account."
          />
        </div>
      </section>
    );
  }

  const filteredToEmpty = query.posture === null && collection.total === 0 && allRows.length > 0;
  const posture = filteredToEmpty ? "empty" : query.posture;
  const gated = posture !== null;
  const showToolbar = query.posture === null || posture === "empty";

  return (
    <section aria-label="History" className="col-span-12 flex flex-col gap-4" data-testid="logs-page">
      {showToolbar ? (
        <div className="space-y-3">
          {gated ? (
            <CollectionToolbar
              collection={collection}
              searchPlaceholder="Filter events"
              className="rounded-xl border border-line"
              stableChipNames
            />
          ) : null}
          <div className="rounded-xl border border-line bg-white px-4 py-3" data-testid="logs-view-select">
            <p className="m-0 text-sm text-ink-muted">{activeView.description}</p>
            {!auditReadable ? (
              <Alert variant="info" className="mt-3">
                The full audit table is staff-only. You'll see the events your account is scoped to read.
              </Alert>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge variant="info">Append-only</Badge>
              <Badge variant="warning">Denied actions recorded</Badge>
            </div>
          </div>
        </div>
      ) : null}

      <div className="rounded-xl border border-line bg-white">
        {gated ? (
          <HonestState
            posture={posture ?? "loading"}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            loadingVariant="rows"
            title={posture === "empty" ? "No events in this view" : undefined}
            message={
              posture === "empty"
                ? "No coaching events match this log view yet. Events appear here as records change; sensitive values are always redacted."
                : undefined
            }
          />
        ) : (
          /* F3: real @vjal/ui ProTable-1 recipe export (not marker-only table chrome). */
          <ProTable
            collection={collection}
            className="p-0"
            tableProps={{
              caption: `Coaching events — ${activeView.label}`,
              emptyTitle: "No events match your search",
              emptyMessage: "Clear or change the filters above to widen the view.",
              tableClassName: "min-w-[1040px]",
            }}
            stableChipNames
          />
        )}
      </div>
    </section>
  );
}
